import { parseAmount, type AccountDto, type ReconcileResultDto } from '@koverta/shared';
import { useState } from 'react';
import { useToast } from '../components/toast';
import { Dialog, MoneyInput } from '../components/ui';
import { errorMessage, post } from '../lib/api';
import { useHousehold } from '../lib/household';
import { invalidateScopes } from '../lib/queries';

/** Reconcile flow (screens.md §4): confirm the cleared balance, or enter the statement and adjust. */
export function ReconcileDialog({ account, onClose }: { account: AccountDto; onClose: () => void }) {
  const { fmt } = useHousehold();
  const { toast } = useToast();
  const [step, setStep] = useState<'ask' | 'enter' | 'confirm'>('ask');
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const statement = parseAmount(text, fmt.locale);
  const diff = statement !== null ? statement - account.cleared : 0;
  const run = async (balance: number) => {
    try {
      const r = await post<ReconcileResultDto>(`/api/accounts/${account.id}/reconcile`, {
        statementBalance: balance,
      });
      toast(
        r.adjustment
          ? `Reconciled · adjustment ${fmt.money(r.adjustment.amount, account.currency, 2)}`
          : `Reconciled ${r.reconciled} transactions`,
      );
      invalidateScopes(['transactions', 'accounts', 'plan', 'activity']);
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <Dialog title={`Reconcile ${account.name}`} onClose={onClose} size="sm">
      {step === 'ask' ? (
        <>
          <div style={{ fontSize: 15 }}>
            Is your cleared balance <b>{fmt.money(account.cleared, account.currency, 2)}</b>?
          </div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            Compare it with your bank app or statement. Cleared transactions are then locked as reconciled.
          </div>
          <div className="row-gap" style={{ justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setStep('enter')}>
              No
            </button>
            <button className="btn primary" onClick={() => run(account.cleared)}>
              Yes
            </button>
          </div>
        </>
      ) : null}
      {step !== 'ask' ? (
        <>
          <label className="field">
            Statement balance ({account.currency})
            <MoneyInput
              className="input r"
              autoFocus
              value={text}
              onChange={(t) => (setText(t), setStep('enter'))}
            />
          </label>
          {statement !== null && diff !== 0 ? (
            <div style={{ fontSize: 14 }}>
              Create adjustment of{' '}
              <b className={diff < 0 ? 'neg-t' : 'pos-t'}>{fmt.money(diff, account.currency, 2, 'always')}</b>
              ?{' '}
              <span className="muted" style={{ fontSize: 12.5 }}>
                It goes to Ready to Assign.
              </span>
            </div>
          ) : null}
          <div className="row-gap" style={{ justifyContent: 'flex-end' }}>
            <button className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={statement === null}
              onClick={() => statement !== null && run(statement)}
            >
              {diff ? 'Create adjustment & reconcile' : 'Reconcile'}
            </button>
          </div>
        </>
      ) : null}
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}
